import { useRequiredContext } from "@redotech/react-util/context";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import { Flex } from "../flex";
import * as styles from "./setting-section-tab-nav.module.css";
import { TabNavigationContext } from "./tab-navigation-provider";

const SMOOTH_SCROLL_FALLBACK_MS = 1000;

/** Nearest ancestor that actually scrolls, so we can listen for its scroll. */
function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export function SettingSectionTabNav({
  rootMargin = "-200px 0px -50% 0px",
  activationOffset = 24,
}: {
  /** Band within the viewport that counts as "current". */
  rootMargin?: string;
  /**
   * Distance from the top of the viewport that marks the "current" line. The
   * active section is the last one scrolled above it, so short panes (where no
   * section ever reaches a narrow band) still track correctly.
   */
  activationOffset?: number;
} = {}) {
  const { sectionRefs, sectionErrors } =
    useRequiredContext(TabNavigationContext);
  const [activeSection, setActiveSection] = useState<string>("");
  // While a click-driven smooth scroll is in flight the scroll tracker would
  // pass through every section on the way, so it stays muted until it lands.
  const jumpTargetRef = useRef<string | null>(null);

  const eleList = useMemo(() => {
    return Object.entries(sectionRefs)
      .filter(([, { ref }]) => ref !== null)
      .sort(([, { ref: refA }], [, { ref: refB }]) => {
        if (!refA || !refB) {
          return 0;
        }
        const topA = refA.getBoundingClientRect().top + window.scrollY;
        const topB = refB.getBoundingClientRect().top + window.scrollY;
        return topA - topB;
      });
  }, [sectionRefs]);

  useEffect(() => {
    const firstRef = Object.values(sectionRefs).find(({ ref }) => ref)?.ref;
    const scroller = firstRef ? findScrollParent(firstRef) : null;

    // Active section = the last one whose top has scrolled above the activation
    // line, measured from the top of whatever actually scrolls (the pane, or
    // the window). Always resolves, so it never sticks on the first section in
    // a short pane the way a narrow intersection band did.
    const computeActive = () => {
      if (jumpTargetRef.current) {
        return;
      }
      const line =
        (scroller ? scroller.getBoundingClientRect().top : 0) +
        activationOffset;
      const sorted = Object.entries(sectionRefs)
        .filter(([, { ref }]) => ref !== null)
        .sort(
          ([, a], [, b]) =>
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            a.ref!.getBoundingClientRect().top -
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            b.ref!.getBoundingClientRect().top,
        );
      if (sorted.length === 0) {
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      let activeId = sorted[0]![0];
      for (const [id, { ref }] of sorted) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (ref!.getBoundingClientRect().top <= line) {
          activeId = id;
        } else {
          break;
        }
      }
      // The last section may be too short to ever cross the line, so the
      // bottom of the scroller counts as reaching it.
      const scrollElement = scroller ?? document.scrollingElement;
      if (
        scrollElement &&
        scrollElement.scrollTop + scrollElement.clientHeight >=
          scrollElement.scrollHeight - 2
      ) {
        activeId = sorted.at(-1)?.[0] ?? activeId;
      }

      setActiveSection(activeId);
    };

    computeActive();

    const observer = new IntersectionObserver(computeActive, {
      threshold: [0, 1],
      rootMargin,
    });
    for (const { ref } of Object.values(sectionRefs)) {
      if (ref) {
        observer.observe(ref);
      }
    }

    scroller?.addEventListener("scroll", computeActive, { passive: true });
    window.addEventListener("scroll", computeActive, {
      passive: true,
      capture: true,
    });

    return () => {
      observer.disconnect();
      scroller?.removeEventListener("scroll", computeActive);
      window.removeEventListener("scroll", computeActive, { capture: true });
    };
  }, [sectionRefs, rootMargin, activationOffset]);

  const jumpTo = (id: string, elementRef: HTMLElement | null) => {
    if (!elementRef) {
      return;
    }
    jumpTargetRef.current = id;
    setActiveSection(id);
    elementRef.scrollIntoView({ behavior: "smooth", block: "start" });
    const release = () => {
      jumpTargetRef.current = null;
      document.removeEventListener("scrollend", release, true);
      window.clearTimeout(fallback);
    };
    const fallback = window.setTimeout(release, SMOOTH_SCROLL_FALLBACK_MS);
    document.addEventListener("scrollend", release, {
      capture: true,
      once: true,
    });
  };

  return (
    <div className={styles.sideNavContainer}>
      <Flex dir="column" gap="none" w="full">
        {eleList.map(([id, { title, ref }]) => (
          <SettingSectionTabNavItem
            active={activeSection === id}
            hasError={(sectionErrors[id]?.length ?? 0) > 0}
            key={id}
            onSelect={() => jumpTo(id, ref)}
            title={title}
          />
        ))}
      </Flex>
    </div>
  );
}

function SettingSectionTabNavItem({
  title,
  active,
  hasError,
  onSelect,
}: {
  title: string;
  active: boolean;
  hasError: boolean;
  onSelect: () => void;
}) {
  return (
    <Flex
      align="center"
      className={classNames(styles.sideNavLink, active && styles.active)}
      gap="xs"
      onClick={onSelect}
    >
      {title}
      {hasError && <span className={styles.errorDot} />}
    </Flex>
  );
}
